import React from "react";
import { legalCompanyDetails } from "./constants";
import type { LegalDocumentDefinition } from "./types";

const { brandName, contactEmail, serviceName } = legalCompanyDetails;

export const disclaimerDocument: LegalDocumentDefinition = {
  id: "disclaimer",
  title: "Disclaimer",
  slug: "/disclaimer",
  effectiveDate: "2026-07-15",
  render: (renderLink) => (
    <>
      <p>
        This Disclaimer explains important limits that apply to information and features provided through {serviceName}.
        It should be read together with our {renderLink({ href: "/tos", children: "Terms of Service" })} and{" "}
        {renderLink({ href: "/privacy", children: "Privacy Policy" })}.
      </p>

      <h5>1. Informational use only</h5>
      <p>
        {brandName} provides meal planning, recipe, shopping, nutrition, and household food management tools for general
        informational and organisational purposes only. The service is not medical, nutrition, dietetic, allergy,
        safety, or other professional advice.
      </p>

      <h5>2. Food, allergy, and ingredient checks</h5>
      <p>
        You are responsible for checking ingredients, labels, preparation methods, allergens, warnings, expiry dates,
        storage instructions, and suitability for anyone who may eat food planned or prepared using the service. Product
        formulations, labels, and supplier information can change, so information in the app should not replace checking
        the product or ingredient source directly.
      </p>

      <h5>3. Nutrition and health information</h5>
      <p>
        Nutrition, macro, five-a-day, portion, and dietary information may be estimated, incomplete, outdated, or based
        on third-party data. Do not rely on the service to diagnose, treat, manage, or prevent any medical condition, or
        to make decisions about allergies, intolerances, pregnancy, medication, eating disorders, specialist diets, or
        other health needs. Seek advice from a qualified professional where appropriate.
      </p>

      <h5>4. Data sources and attribution</h5>
      <p>
        Nutrition data includes information from McCance and Widdowson&rsquo;s Composition of Foods Integrated Dataset
        (CoFID) 2021, &copy; Crown copyright, licensed under the{" "}
        <a
          href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
          target="_blank"
          rel="noreferrer"
        >
          Open Government Licence v3.0
        </a>
        .
      </p>
      <p>
        Nutrition data also includes information from the U.S. Department of Agriculture, Agricultural Research
        Service,{" "}
        <a href="https://fdc.nal.usda.gov/" target="_blank" rel="noreferrer">
          FoodData Central
        </a>
        , released under CC0.
      </p>

      <h5>5. AI-assisted content</h5>
      <p>
        Some features may use AI or automated processing to suggest recipes, ingredients, meal plans, shopping items, or
        other content. AI-assisted outputs can be inaccurate, incomplete, or unsuitable for your circumstances. Review
        all outputs before using them, especially where allergies, nutrition, health, budget, or food safety are relevant.
      </p>

      <h5>6. Third-party information and services</h5>
      <p>
        The service may include links, integrations, or information from third-party providers. We do not control and are
        not responsible for third-party content, availability, policies, or advice.
      </p>

      <h5>7. Contact</h5>
      <p>
        Questions about this Disclaimer can be sent to <code>{contactEmail}</code>.
      </p>
    </>
  ),
};
